// src/pages/AISentryMobile.jsx
// Mobile-optimized AI Sentry - Shift Briefing + Chat + Events + Alerts
// Designed for iPhone/iPad - Uses same services as desktop

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { edgeNodesService } from '../services/edge-nodes';
import { eventsService } from '../services/events';
import { useAuth } from '../components/auth/AuthContext';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const API_KEY = process.env.REACT_APP_API_KEY || '';
const apiHeaders = {
  'Content-Type': 'application/json',
  ...(API_KEY ? { 'X-API-Key': API_KEY } : {}),
};

// ════════════════════════════════════════════════════════════════════════════
// SEVERITY COLORS
// ════════════════════════════════════════════════════════════════════════════

const SEVERITY_STYLES = {
  Critical: { bg: 'bg-red-500', text: 'text-white' },
  High: { bg: 'bg-orange-500', text: 'text-white' },
  Warning: { bg: 'bg-yellow-500', text: 'text-yellow-900' },
  Notice: { bg: 'bg-blue-500', text: 'text-white' },
  Informational: { bg: 'bg-slate-500', text: 'text-white' },
};

// ════════════════════════════════════════════════════════════════════════════
// HELPER: Format timestamp
// ════════════════════════════════════════════════════════════════════════════

const formatTimestamp = (timestamp) => {
  if (!timestamp) return '';
  const date = new Date(timestamp + (timestamp.endsWith('Z') ? '' : 'Z'));
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();
  
  if (isToday) {
    return date.toLocaleString('en-US', {
      timeZone: 'America/Chicago',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  }
  
  return date.toLocaleString('en-US', {
    timeZone: 'America/Chicago',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
};

// ════════════════════════════════════════════════════════════════════════════
// HELPER: Detect threats from events
// ════════════════════════════════════════════════════════════════════════════

const detectThreatsHeuristic = (events) => {
  const threats = [];
  const bySource = {};
  
  events.forEach(event => {
    const sourceIp = event.source_ip;
    const destIp = event.destination_ip;
    const severity = event.severity;
    const policyName = event.details?.policy_name || '';
    
    if (!sourceIp || sourceIp === 'N/A' || !destIp || destIp === 'N/A') return;
    
    if (!bySource[sourceIp]) {
      bySource[sourceIp] = { targets: new Set(), criticalCount: 0, policies: new Set(), events: [], agentId: null };
    }
    
    bySource[sourceIp].targets.add(destIp);
    bySource[sourceIp].events.push(event);
    if (policyName) bySource[sourceIp].policies.add(policyName);
    if (severity === 'Critical') bySource[sourceIp].criticalCount++;
    if (event.celerway_edge_node_id) bySource[sourceIp].agentId = event.celerway_edge_node_id;
  });

  Object.entries(bySource).forEach(([sourceIp, data]) => {
    const policies = Array.from(data.policies);
    const hasUnauthorized = policies.some(p => 
      p.toLowerCase().includes('unauthorized') || p.toLowerCase().includes('critical infrastructure')
    );
    
    if (data.criticalCount > 0 && hasUnauthorized) {
      threats.push({
        id: `isolation-${sourceIp}`,
        type: 'ISOLATION',
        device: sourceIp,
        targetDevices: Array.from(data.targets),
        reason: 'Unauthorized access blocked',
        confidence: Math.min(98, 85 + data.criticalCount * 3),
        severity: 'critical',
        agentId: data.agentId,
      });
    } else if (data.targets.size >= 3 && data.events.length >= 10) {
      threats.push({
        id: `scan-${sourceIp}`,
        type: 'RECOMMENDATION',
        device: sourceIp,
        targetDevices: Array.from(data.targets),
        reason: 'Port scan pattern detected',
        confidence: Math.min(90, 70 + data.targets.size * 4),
        severity: 'high',
        agentId: data.agentId,
      });
    }
  });

  return threats.sort((a, b) => {
    const sevOrder = { critical: 0, high: 1, medium: 2 };
    return (sevOrder[a.severity] || 3) - (sevOrder[b.severity] || 3);
  });
};

// ════════════════════════════════════════════════════════════════════════════
// HEADER COMPONENT
// ════════════════════════════════════════════════════════════════════════════

const MobileHeader = ({ stats, onRefresh, isRefreshing, selectedAgent, edgeNodes, onSelectAgent, onLogout }) => (
  <header className="sticky top-0 z-50 bg-slate-900 border-b border-slate-700 px-4 py-3">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-gradient-to-br from-cyan-500/30 to-blue-600/30 rounded-xl flex items-center justify-center">
          <span className="text-xl">🤖</span>
        </div>
        <div>
          <h1 className="text-lg font-bold text-white">AI Sentry</h1>
          <p className="text-xs text-slate-400">AI Sentry Mobile</p>
        </div>
      </div>
      
      <div className="flex items-center gap-2">
        {stats.alertCount > 0 && (
          <div className="flex items-center gap-1 bg-red-500/20 rounded-lg px-2 py-1 animate-pulse">
            <span className="text-red-400 text-sm font-bold">{stats.alertCount}</span>
          </div>
        )}
        
        <button 
          onClick={onRefresh}
          disabled={isRefreshing}
          className="w-8 h-8 bg-slate-800 rounded-lg flex items-center justify-center"
        >
          <svg 
            className={`w-4 h-4 text-slate-400 ${isRefreshing ? 'animate-spin' : ''}`} 
            fill="none" stroke="currentColor" viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        </button>
        
        {/* Logout button */}
        <button 
          onClick={onLogout}
          className="w-8 h-8 bg-slate-800 rounded-lg flex items-center justify-center"
          title="Logout"
        >
          <svg className="w-4 h-4 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
          </svg>
        </button>
      </div>
    </div>
    
    {/* Edge Node Selector */}
    <div className="mt-3">
      <select
        value={selectedAgent || ''}
        onChange={(e) => onSelectAgent(e.target.value)}
        className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm"
      >
        <option value="">Select Edge Agent...</option>
        {edgeNodes.map(node => (
          <option key={node.edge_node_id} value={node.edge_node_id}>
            {node.edge_node_id} {node.status === 'Online' ? '🟢' : '🔴'}
          </option>
        ))}
      </select>
    </div>
  </header>
);

// ════════════════════════════════════════════════════════════════════════════
// SHIFT BRIEFING CARD
// ════════════════════════════════════════════════════════════════════════════

const ShiftBriefingCard = ({ briefing, isLoading, isExpanded, onToggle, onExpand, expandedContent, isLoadingExpanded }) => (
  <div className="mx-4 mt-4">
    <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl border border-cyan-500/30 overflow-hidden">
      <button onClick={onToggle} className="w-full px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-cyan-500/20 rounded-lg flex items-center justify-center">
            <span>📋</span>
          </div>
          <span className="text-white font-semibold">Shift Briefing</span>
        </div>
        <svg 
          className={`w-5 h-5 text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
          fill="none" stroke="currentColor" viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      
      {isExpanded && (
        <div className="px-4 pb-4 border-t border-slate-700/50">
          {isLoading ? (
            <div className="py-6 text-center">
              <div className="w-6 h-6 border-2 border-cyan-400/30 border-t-cyan-400 rounded-full animate-spin mx-auto" />
              <p className="text-slate-400 text-sm mt-2">AI Sentry analyzing...</p>
            </div>
          ) : (
            <>
              <p className="text-slate-200 text-sm leading-relaxed py-3">
                {briefing || "Select an edge agent to generate briefing."}
              </p>
              
              {briefing && !expandedContent && (
                <button
                  onClick={onExpand}
                  disabled={isLoadingExpanded}
                  className="w-full mt-2 py-2 bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-300 text-sm font-medium rounded-lg"
                >
                  {isLoadingExpanded ? 'Loading...' : 'Show Detailed Analysis'}
                </button>
              )}
              
              {expandedContent && (
                <div className="mt-3 pt-3 border-t border-slate-700/50">
                  <p className="text-slate-300 text-sm whitespace-pre-wrap leading-relaxed">{expandedContent}</p>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  </div>
);

// ════════════════════════════════════════════════════════════════════════════
// PROACTIVE ALERT BANNER
// ════════════════════════════════════════════════════════════════════════════

const ProactiveAlertBanner = ({ threat, onViewDetails }) => {
  if (!threat) return null;
  
  return (
    <div className="mx-4 mt-4">
      <div className="bg-gradient-to-r from-red-900/80 to-red-800/80 border border-red-500/50 rounded-2xl p-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-red-500/30 rounded-full flex items-center justify-center flex-shrink-0">
            <span className="text-xl">🚨</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white font-semibold">Brian, you need to see this</p>
            <p className="text-red-200 text-sm mt-1">{threat.reason}</p>
            <p className="text-red-300/70 text-xs mt-1 font-mono">{threat.device} → {threat.targetDevices?.length || 0} target(s)</p>
          </div>
        </div>
        
        <button
          onClick={onViewDetails}
          className="w-full mt-4 py-3 bg-red-500 hover:bg-red-400 text-white font-semibold rounded-xl"
        >
          View Details
        </button>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// EVENTS LIST (Mobile-friendly cards)
// ════════════════════════════════════════════════════════════════════════════

const EventsList = ({ events, onEventTap }) => {
  if (!events || events.length === 0) {
    return (
      <div className="mx-4 mt-4 bg-slate-800/50 rounded-xl p-6 text-center">
        <span className="text-3xl">📭</span>
        <p className="text-slate-400 mt-2">No events found</p>
      </div>
    );
  }
  
  return (
    <div className="mx-4 mt-4">
      <h2 className="text-slate-400 text-xs font-semibold uppercase tracking-wider mb-3 px-1">
        Recent Events ({events.length})
      </h2>
      
      <div className="space-y-2">
        {events.slice(0, 25).map((event, idx) => {
          const severity = SEVERITY_STYLES[event.severity] || SEVERITY_STYLES.Informational;
          
          return (
            <button
              key={event.event_id || idx}
              onClick={() => onEventTap(event)}
              className="w-full bg-slate-800/80 hover:bg-slate-700/80 rounded-xl p-3 text-left"
            >
              <div className="flex items-start gap-3">
                <div className={`w-2 h-2 rounded-full mt-2 ${severity.bg}`} />
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-400 text-xs">{formatTimestamp(event.timestamp)}</span>
                    <span className={`text-xs px-2 py-0.5 rounded ${severity.bg} ${severity.text}`}>
                      {event.severity}
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-white font-mono text-sm">{event.source_ip || 'N/A'}</span>
                    <span className="text-slate-500">→</span>
                    <span className="text-cyan-300 font-mono text-sm">{event.destination_ip || 'N/A'}</span>
                  </div>
                  
                  {event.details?.policy_name && (
                    <p className="text-slate-400 text-xs mt-1 truncate">{event.details.policy_name}</p>
                  )}
                  
                  {event.protocol && (
                    <span className="inline-block mt-2 text-xs px-2 py-0.5 bg-blue-500/20 text-blue-300 rounded">
                      {event.protocol}
                    </span>
                  )}
                </div>
                
                <svg className="w-4 h-4 text-slate-500 mt-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// EVENT DETAIL MODAL
// ════════════════════════════════════════════════════════════════════════════

const EventDetailModal = ({ event, onClose }) => {
  if (!event) return null;
  const severity = SEVERITY_STYLES[event.severity] || SEVERITY_STYLES.Informational;
  
  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      
      <div className="absolute bottom-0 left-0 right-0 bg-slate-900 rounded-t-3xl max-h-[85vh] overflow-hidden animate-slide-up">
        <div className="flex justify-center py-3">
          <div className="w-12 h-1.5 bg-slate-700 rounded-full" />
        </div>
        
        <div className="px-4 pb-3 flex items-center justify-between border-b border-slate-800">
          <h2 className="text-white font-bold text-lg">Event Details</h2>
          <button onClick={onClose} className="text-slate-400">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <div className="p-4 overflow-y-auto max-h-[70vh] space-y-4">
          <div className="flex items-center justify-between">
            <span className={`text-sm px-3 py-1 rounded-full ${severity.bg} ${severity.text}`}>{event.severity}</span>
            <span className="text-slate-400 text-sm">{formatTimestamp(event.timestamp)}</span>
          </div>
          
          <div className="bg-slate-800 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-xs">Source</p>
                <p className="text-white font-mono">{event.source_ip || 'N/A'}</p>
              </div>
              <span className="text-2xl">→</span>
              <div className="text-right">
                <p className="text-slate-400 text-xs">Destination</p>
                <p className="text-cyan-300 font-mono">{event.destination_ip || 'N/A'}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-slate-800 rounded-xl p-4">
            <p className="text-slate-400 text-xs mb-1">Protocol</p>
            <p className="text-white">{event.protocol || 'N/A'}</p>
          </div>
          
          <div className="bg-slate-800 rounded-xl p-4">
            <p className="text-slate-400 text-xs mb-1">Edge Agent</p>
            <p className="text-white">{event.celerway_edge_node_id || 'N/A'}</p>
          </div>
          
          {event.details?.policy_name && (
            <div className="bg-slate-800 rounded-xl p-4">
              <p className="text-slate-400 text-xs mb-1">Policy</p>
              <p className="text-white">{event.details.policy_name}</p>
              {event.details.rule_name && <p className="text-slate-400 text-sm mt-1">{event.details.rule_name}</p>}
            </div>
          )}
          
          {event.description && (
            <div className="bg-slate-800 rounded-xl p-4">
              <p className="text-slate-400 text-xs mb-1">Description</p>
              <p className="text-white text-sm">{event.description}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// ACTION CARD
// ════════════════════════════════════════════════════════════════════════════

const ActionCard = ({ action, onApprove, onDismiss, isExecuting }) => (
  <div className="bg-slate-800/80 rounded-xl p-4 border border-slate-700">
    <div className="flex items-start gap-3">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
        action.type === 'ISOLATION' ? 'bg-red-500/30' : 'bg-yellow-500/30'
      }`}>
        <span className="text-lg">{action.type === 'ISOLATION' ? '🛡️' : '⚠️'}</span>
      </div>
      
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between">
          <p className="text-white font-semibold text-sm">{action.type.replace('_', ' ')}</p>
          <span className="text-xs font-bold text-cyan-400">{action.confidence}%</span>
        </div>
        <p className="text-cyan-300 font-mono text-sm mt-1">{action.device}</p>
        <p className="text-slate-400 text-xs mt-1">{action.reason}</p>
      </div>
    </div>
    
    <div className="flex gap-3 mt-4">
      <button
        onClick={() => onDismiss(action)}
        disabled={isExecuting}
        className="flex-1 py-2.5 bg-slate-700 hover:bg-slate-600 text-slate-300 font-medium rounded-xl"
      >
        Dismiss
      </button>
      <button
        onClick={() => onApprove(action)}
        disabled={isExecuting}
        className="flex-1 py-2.5 bg-emerald-500 hover:bg-emerald-400 text-white font-semibold rounded-xl disabled:opacity-50"
      >
        {isExecuting ? 'Executing...' : 'Approve'}
      </button>
    </div>
  </div>
);

// ════════════════════════════════════════════════════════════════════════════
// ACTIONS PANEL
// ════════════════════════════════════════════════════════════════════════════

const ActionsPanel = ({ isOpen, onClose, actions, onApprove, onDismiss, isExecuting }) => {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      
      <div className="absolute bottom-0 left-0 right-0 bg-slate-900 rounded-t-3xl max-h-[80vh] overflow-hidden animate-slide-up">
        <div className="flex justify-center py-3">
          <div className="w-12 h-1.5 bg-slate-700 rounded-full" />
        </div>
        
        <div className="px-4 pb-3 flex items-center justify-between border-b border-slate-800">
          <h2 className="text-white font-bold text-lg">AI Recommendations</h2>
          <button onClick={onClose} className="text-slate-400">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <div className="p-4 space-y-3 overflow-y-auto max-h-[60vh]">
          {actions.length === 0 ? (
            <div className="text-center py-8">
              <span className="text-4xl">✅</span>
              <p className="text-slate-400 mt-3">No pending actions</p>
            </div>
          ) : (
            actions.map((action, idx) => (
              <ActionCard key={idx} action={action} onApprove={onApprove} onDismiss={onDismiss} isExecuting={isExecuting} />
            ))
          )}
        </div>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// CHAT INTERFACE
// ════════════════════════════════════════════════════════════════════════════

const ChatInterface = ({ isOpen, onClose }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);
  
  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;
    
    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);
    
    try {
      const response = await axios.post(`${API_BASE}/chat/`, {
        message: userMessage,
        history: messages.slice(-10),
        session_id: `mobile-${Date.now()}`
      }, { headers: apiHeaders });
      setMessages(prev => [...prev, { role: 'assistant', content: response.data.content }]);
    } catch (error) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, I encountered an error.' }]);
    } finally {
      setIsLoading(false);
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 bg-slate-900 flex flex-col">
      <header className="flex items-center gap-3 px-4 py-3 border-b border-slate-800">
        <button onClick={onClose} className="text-slate-400">
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-cyan-500/20 rounded-lg flex items-center justify-center">
            <span>💬</span>
          </div>
          <span className="text-white font-semibold">AI Sentry Chat</span>
        </div>
      </header>
      
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 && (
          <div className="text-center py-8">
            <span className="text-4xl">🤖</span>
            <p className="text-slate-400 mt-3">Ask me about your OT security</p>
            <div className="mt-4 space-y-2">
              {["What threats were detected?", "Show unauthorized access", "Security briefing"].map((prompt, idx) => (
                <button 
                  key={idx}
                  onClick={() => setInput(prompt)}
                  className="w-full text-left px-4 py-2 bg-slate-800 rounded-lg text-cyan-300 text-sm"
                >
                  "{prompt}"
                </button>
              ))}
            </div>
          </div>
        )}
        
        {messages.map((msg, idx) => (
          <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] rounded-2xl px-4 py-3 ${
              msg.role === 'user' ? 'bg-cyan-500 text-white' : 'bg-slate-800 text-slate-200'
            }`}>
              <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
            </div>
          </div>
        ))}
        
        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-slate-800 rounded-2xl px-4 py-3">
              <div className="flex gap-1">
                <div className="w-2 h-2 bg-cyan-400 rounded-full animate-bounce" />
                <div className="w-2 h-2 bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <div className="w-2 h-2 bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>
      
      <div className="p-4 border-t border-slate-800 pb-8">
        <div className="flex gap-2">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
            placeholder="Ask AI Sentry..."
            className="flex-1 bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500"
          />
          <button
            onClick={sendMessage}
            disabled={!input.trim() || isLoading}
            className="w-12 h-12 bg-cyan-500 hover:bg-cyan-400 disabled:opacity-50 rounded-xl flex items-center justify-center"
          >
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// BOTTOM NAV
// ════════════════════════════════════════════════════════════════════════════

const BottomNav = ({ activeTab, onTabChange, actionsCount }) => (
  <nav className="fixed bottom-0 left-0 right-0 bg-slate-900 border-t border-slate-800 px-6 py-2 pb-6 z-40">
    <div className="flex justify-around">
      {[
        { id: 'home', icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6', label: 'Home' },
        { id: 'events', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2', label: 'Events' },
        { id: 'actions', icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z', label: 'Actions', badge: actionsCount },
        { id: 'chat', icon: 'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z', label: 'Chat' },
      ].map(tab => (
        <button
          key={tab.id}
          onClick={() => onTabChange(tab.id)}
          className={`flex flex-col items-center gap-1 py-2 px-4 relative ${activeTab === tab.id ? 'text-cyan-400' : 'text-slate-500'}`}
        >
          {tab.badge > 0 && (
            <span className="absolute -top-1 right-2 w-5 h-5 bg-red-500 rounded-full text-white text-xs flex items-center justify-center">
              {tab.badge}
            </span>
          )}
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={tab.icon} />
          </svg>
          <span className="text-xs">{tab.label}</span>
        </button>
      ))}
    </div>
  </nav>
);

// ════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ════════════════════════════════════════════════════════════════════════════

const AISentryMobile = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState('home');
  const [isActionsOpen, setIsActionsOpen] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isBriefingExpanded, setIsBriefingExpanded] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isExecuting, setIsExecuting] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  
  const [edgeNodes, setEdgeNodes] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [events, setEvents] = useState([]);
  const [threats, setThreats] = useState([]);
  const [briefing, setBriefing] = useState(null);
  const [expandedBriefing, setExpandedBriefing] = useState(null);
  const [isLoadingBriefing, setIsLoadingBriefing] = useState(false);
  const [isLoadingExpanded, setIsLoadingExpanded] = useState(false);
  const [stats, setStats] = useState({ onlineAgents: 0, alertCount: 0 });
  
  // Handle logout
  const handleLogout = useCallback(() => {
    logout();
    navigate('/login', { replace: true });
  }, [logout, navigate]);
  
  // Fetch edge nodes
  useEffect(() => {
    const fetchEdgeNodes = async () => {
      try {
        const response = await edgeNodesService.getEdgeNodes();
        const nodes = response.data || response || [];
        setEdgeNodes(nodes);
        
        const onlineAgent = nodes.find(n => n.status === 'Online');
        if (onlineAgent) setSelectedAgent(onlineAgent.edge_node_id);
        else if (nodes.length > 0) setSelectedAgent(nodes[0].edge_node_id);
        
        setStats(prev => ({ ...prev, onlineAgents: nodes.filter(n => n.status === 'Online').length }));
      } catch (error) {
        console.error('Failed to fetch edge nodes:', error);
      }
    };
    fetchEdgeNodes();
  }, []);
  
  // Fetch events when agent changes
  useEffect(() => {
    const fetchEvents = async () => {
      if (!selectedAgent) return;
      setIsRefreshing(true);
      try {
        const response = await eventsService.getEvents({ limit: 100 });
        const allEvents = response.data || response || [];
        const filteredEvents = allEvents.filter(e => e.celerway_edge_node_id === selectedAgent);
        setEvents(filteredEvents);
        
        const detectedThreats = detectThreatsHeuristic(filteredEvents);
        setThreats(detectedThreats);
        setStats(prev => ({ ...prev, alertCount: detectedThreats.length }));
      } catch (error) {
        console.error('Failed to fetch events:', error);
      } finally {
        setIsRefreshing(false);
      }
    };
    fetchEvents();
  }, [selectedAgent]);
  
  // Fetch briefing
  const fetchBriefing = useCallback(async () => {
    if (!selectedAgent) return;
    setIsLoadingBriefing(true);
    try {
      const response = await axios.post(`${API_BASE}/chat/`, {
        message: 'Generate a brief shift change security briefing (2-3 sentences). Start with "Attention needed:" if threats exist, or "All clear:" if secure.',
        history: [],
        session_id: `mobile-briefing-${Date.now()}`
      }, { headers: apiHeaders });
      setBriefing(response.data.content);
    } catch (error) {
      setBriefing('Unable to generate briefing.');
    } finally {
      setIsLoadingBriefing(false);
    }
  }, [selectedAgent]);
  
  useEffect(() => {
    if (selectedAgent) fetchBriefing();
  }, [selectedAgent, fetchBriefing]);
  
  const fetchExpandedBriefing = useCallback(async () => {
    setIsLoadingExpanded(true);
    try {
      const response = await axios.post(`${API_BASE}/chat/`, {
        message: 'Generate a detailed security analysis with threat summary, affected assets, and recommended actions.',
        history: [],
        session_id: `mobile-expanded-${Date.now()}`
      }, { headers: apiHeaders });
      setExpandedBriefing(response.data.content);
    } catch (error) {
      console.error('Expanded briefing error:', error);
    } finally {
      setIsLoadingExpanded(false);
    }
  }, []);
  
  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    setBriefing(null);
    setExpandedBriefing(null);
    try {
      const response = await eventsService.getEvents({ limit: 100 });
      const allEvents = response.data || response || [];
      const filteredEvents = selectedAgent ? allEvents.filter(e => e.celerway_edge_node_id === selectedAgent) : allEvents;
      setEvents(filteredEvents);
      const detectedThreats = detectThreatsHeuristic(filteredEvents);
      setThreats(detectedThreats);
      setStats(prev => ({ ...prev, alertCount: detectedThreats.length }));
      await fetchBriefing();
    } catch (error) {
      console.error('Refresh error:', error);
    } finally {
      setIsRefreshing(false);
    }
  }, [selectedAgent, fetchBriefing]);
  
  const handleApprove = useCallback(async (action) => {
    setIsExecuting(true);
    try {
      await axios.post(`${API_BASE}/nimbus/action/`, {
        action_type: 'vpn_profile_change',
        params: { agent_id: action.agentId || selectedAgent, threat_source: action.device, reason: action.reason }
      }, { headers: apiHeaders });
      setThreats(prev => prev.filter(t => t.id !== action.id));
    } catch (error) {
      console.error('Approve error:', error);
    } finally {
      setIsExecuting(false);
    }
  }, [selectedAgent]);
  
  const handleDismiss = useCallback((action) => {
    setThreats(prev => prev.filter(t => t.id !== action.id));
  }, []);
  
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'actions') setIsActionsOpen(true);
    if (tab === 'chat') setIsChatOpen(true);
  };
  
  const criticalThreat = threats.find(t => t.type === 'ISOLATION');
  const actionableThreats = threats.filter(t => t.type === 'ISOLATION' || t.type === 'RECOMMENDATION');
  
  return (
    <div className="min-h-screen bg-slate-900 text-white pb-24">
      <MobileHeader 
        stats={stats} 
        onRefresh={handleRefresh} 
        isRefreshing={isRefreshing}
        selectedAgent={selectedAgent}
        edgeNodes={edgeNodes}
        onSelectAgent={setSelectedAgent}
        onLogout={handleLogout}
      />
      
      {activeTab === 'home' && (
        <>
          <ProactiveAlertBanner threat={criticalThreat} onViewDetails={() => { setIsActionsOpen(true); setActiveTab('actions'); }} />
          <ShiftBriefingCard
            briefing={briefing}
            isLoading={isLoadingBriefing}
            isExpanded={isBriefingExpanded}
            onToggle={() => setIsBriefingExpanded(!isBriefingExpanded)}
            onExpand={fetchExpandedBriefing}
            expandedContent={expandedBriefing}
            isLoadingExpanded={isLoadingExpanded}
          />
          <div className="mx-4 mt-4 grid grid-cols-3 gap-2">
            <div className="bg-slate-800 rounded-xl p-3 text-center">
              <p className="text-2xl font-bold text-emerald-400">{stats.onlineAgents}</p>
              <p className="text-slate-400 text-xs">Agents</p>
            </div>
            <div className="bg-slate-800 rounded-xl p-3 text-center">
              <p className="text-2xl font-bold text-blue-400">{events.length}</p>
              <p className="text-slate-400 text-xs">Events</p>
            </div>
            <div className="bg-slate-800 rounded-xl p-3 text-center">
              <p className="text-2xl font-bold text-red-400">{stats.alertCount}</p>
              <p className="text-slate-400 text-xs">Alerts</p>
            </div>
          </div>
        </>
      )}
      
      {activeTab === 'events' && <EventsList events={events} onEventTap={setSelectedEvent} />}
      
      <EventDetailModal event={selectedEvent} onClose={() => setSelectedEvent(null)} />
      <ActionsPanel isOpen={isActionsOpen} onClose={() => { setIsActionsOpen(false); setActiveTab('home'); }} actions={actionableThreats} onApprove={handleApprove} onDismiss={handleDismiss} isExecuting={isExecuting} />
      <ChatInterface isOpen={isChatOpen} onClose={() => { setIsChatOpen(false); setActiveTab('home'); }} />
      <BottomNav activeTab={activeTab} onTabChange={handleTabChange} actionsCount={actionableThreats.length} />
      
      <style>{`
        @keyframes slide-up { from { transform: translateY(100%); } to { transform: translateY(0); } }
        .animate-slide-up { animation: slide-up 0.3s ease-out; }
      `}</style>
    </div>
  );
};

export default AISentryMobile;
